import Button from "components/base/Button";
import { Input, NativeDatePicker, Select, Text } from "components/base/Form";
import { InlineSpinner } from "components/base/Spinner";
import CustomersSelector from "components/shared/CustomerSelector";
import OdooDeliveryMethodSelector from "components/shared/OdooDeliveryMethodSelector";
import OdooIncotermsSelector from "components/shared/OdooIncotermsSelector";
import OdooPaymentTermsSelector from "components/shared/OdooPaymentTermsSelector";
import OdooTaxSelector from "components/shared/OdooTaxSelector";
import OdooUserSelector from "components/shared/OdooUserSelector";
import WisPriceListSelector from "components/shared/WisPriceListSelector";
import { useModals } from "ModalProvider";
import { useContext } from "react";
import { Link } from "react-router-dom";
import ConfirmChangeWarehouseView from "./ConfirmChangeWarehouseView";
import { OrderContext } from "./OrderDetail";

const BaseInfoView = ({
  stockCategories,
  activeCurrencies,
  odooPricelists,
}) => {
  const {
    originalOrder,
    order,
    setOrder,
    changeWarehouse,
    changeWarehouseRes,
    changePricelist,
    changePricelistRes,
  } = useContext(OrderContext);

  const modal = useModals();

  function showCustomerSelector() {
    modal.present({
      title: "Choose Customer",
      center: false,
      children: <CustomersSelector odooOnly onSelect={didSelectCustomer} />,
    });
  }

  function didSelectCustomer(customer) {
    setOrder((prev) => ({ ...prev, customer }));
    modal.hide();
  }

  function confirmChangeWarehouse(stockCategory) {
    changeWarehouse({
      variables: {
        id: order.id,
        input: { stockCategoryId: stockCategory.id },
        orderLines: [],
      },
    });
  }

  function tryChangeWarehouse(e) {
    const stockCategoryId = e.target.value;
    const stockCategory = stockCategories.find((i) => i.id === stockCategoryId);

    if (order.orderLines.length > 0) {
      modal.present({
        title: "Change Warehouse",
        center: true,
        maxWidth: "max-w-3xl",
        children: (
          <ConfirmChangeWarehouseView
            stockCategory={stockCategory}
            orderId={order.id}
            confirmHandler={() => {
              confirmChangeWarehouse(stockCategory);
              modal.hide();
            }}
          />
        ),
      });
    } else {
      confirmChangeWarehouse(stockCategory);
    }
  }

  function onChangeCurrency(e) {
    const newOdooPricelistId = Number(e.target.value);

    if (newOdooPricelistId === 0) {
      setOrder({
        ...order,
        odooPricelistId: newOdooPricelistId,
        exchangeRate: 1,
      });
    } else {
      const newOdooPricelist = odooPricelists.find(
        (i) => i.id === Number(e.target.value),
      );
      const newCurrency = newOdooPricelist.currency;

      const pricelistCurrency = originalOrder.pricelist.currency;
      let exchangeRate = 1;

      if (newCurrency && newCurrency !== pricelistCurrency) {
        const source = activeCurrencies.find(
          (i) => i.name === pricelistCurrency,
        );
        const target = activeCurrencies.find((i) => i.name === newCurrency);
        exchangeRate = Number(((1 / source.rate) * target.rate).toFixed(5));
      }

      setOrder({
        ...order,
        odooPricelistId: newOdooPricelistId,
        exchangeRate,
      });
    }
  }

  return (
    <div className="card">
      <h5>Base Info</h5>
      <div className="mt-4 space-y-3">
        <div className="space-x-3 flex items-center">
          <label>Warehouse: </label>
          <Select
            value={originalOrder.stockCategory?.id || "0"}
            onChange={tryChangeWarehouse}
          >
            <option value="0" disabled>
              Select Warehouse
            </option>
            {stockCategories.map((i) => (
              <option key={i.name} value={i.id}>
                {i.name}
              </option>
            ))}
          </Select>
          {changeWarehouseRes.loading ? (
            <InlineSpinner text={null} size={16} />
          ) : null}

          {originalOrder.stockCategory && (
            <Link
              to={`/sales/inventory/${originalOrder.stockCategory.id}`}
              target="_blank"
            >
              View Stock
            </Link>
          )}
        </div>

        <div className="flex space-x-8">
          <div className="flex space-x-3 items-center">
            <label className="pr-3">Customer: </label>
            <span>
              {order.customer ? (
                <Link
                  to={`/sales/customers/${order.customer.id}`}
                  target="_blank"
                >
                  {order.customer.name}
                </Link>
              ) : (
                "No customer selected"
              )}
            </span>
            <Button onClick={showCustomerSelector}>Choose</Button>
          </div>

          <div className="flex items-center space-x-3">
            <label className="pr-3">Pricelist: </label>
            <WisPriceListSelector
              value={
                originalOrder.pricelist ? originalOrder.pricelist.id : null
              }
              onChange={(pricelist) => {
                changePricelist({
                  variables: {
                    id: order.id,
                    input: { pricelistId: pricelist.id },
                  },
                });
              }}
            />
            {changePricelistRes.loading ? (
              <InlineSpinner text={null} size={16} />
            ) : null}

            {originalOrder.pricelist && (
              <Link
                to={`/sales/price-lists/${originalOrder.pricelist.id}`}
                target="_blank"
              >
                View Pricelist
              </Link>
            )}
          </div>
        </div>

        <div>
          <div className="flex space-x-6">
            <div className="flex items-center space-x-3">
              <label className="pr-3">Odoo Currency: </label>
              <Select value={order.odooPricelistId} onChange={onChangeCurrency}>
                <option value="0">Use Pricelist Currency</option>
                {odooPricelists.map((i) => (
                  <option key={i.name} value={i.id}>
                    {i.currency}
                  </option>
                ))}
              </Select>
            </div>

            {order.odooPricelistId > 0 && (
              <div className="flex items-center space-x-3">
                <label className="pr-3">
                  Exchange Rate ( 1.0 {originalOrder.pricelist.currency} = ?{" "}
                  {order.odooCurrency}
                  ):{" "}
                </label>
                <Input
                  className="text-center"
                  value={order.exchangeRate}
                  onChange={(e) =>
                    setOrder({ ...order, exchangeRate: e.target.value })
                  }
                />
              </div>
            )}
          </div>

          <div className="opacity-70 mt-1">
            Choose Odoo Currency if you want to convert the sales price to
            another currency based on the exchange rate.
          </div>
        </div>

        <div className="flex items-center">
          <label className="pr-3 whitespace-nowrap">Customer Ref: </label>
          <Input
            className="w-full"
            value={order.odooSalesOrderClientOrderRef}
            onChange={(e) =>
              setOrder({
                ...order,
                odooSalesOrderClientOrderRef: e.target.value,
              })
            }
          />
        </div>

        <div>
          <label className="pr-3">Payment Terms: </label>
          <OdooPaymentTermsSelector
            value={order.odooPaymentTermsId}
            onSelect={(odooPaymentTermsId) =>
              setOrder((prev) => ({ ...prev, odooPaymentTermsId }))
            }
            company="AB"
          />
        </div>

        <div>
          <label className="pr-3">Tax:</label>
          <OdooTaxSelector
            value={order.odooTaxId}
            onSelect={(odooTaxId) =>
              setOrder((prev) => ({ ...prev, odooTaxId }))
            }
          />
        </div>

        <div className="flex space-x-8">
          <div>
            <label className="pr-3">Delivery Method: </label>
            <OdooDeliveryMethodSelector
              company="AB"
              value={order.odooXDeliverymethod}
              onSelect={(odooXDeliverymethod) =>
                setOrder((prev) => ({ ...prev, odooXDeliverymethod }))
              }
            />
          </div>

          <div>
            <label className="pr-3">Incoterm: </label>
            <OdooIncotermsSelector
              company="AB"
              value={order.odooIncoterm}
              onSelect={(odooIncoterm) =>
                setOrder((prev) => ({ ...prev, odooIncoterm }))
              }
            />
          </div>
        </div>

        <div className="flex space-x-8">
          <div>
            <label className="pr-3">Delivery Schedule Date:</label>
            <NativeDatePicker
              value={order.odooXDoMinDate}
              onChange={(e) =>
                setOrder({ ...order, odooXDoMinDate: e.target.value })
              }
            />
          </div>

          <div>
            <label className="pr-3">Sales Person:</label>
            <OdooUserSelector
              instance="AB"
              company="AB"
              value={order.odooUserId}
              onSelect={(odooUserId) => setOrder({ ...order, odooUserId })}
            />
          </div>
        </div>

        <div>
          <div className="mb-2">
            <label className="pr-3">Notes: </label>
          </div>
          <Text
            value={order.notes}
            onChange={(e) => setOrder({ ...order, notes: e.target.value })}
            placeholder="Remark"
          />
        </div>
      </div>
    </div>
  );
};

export default BaseInfoView;
